import { doc, runTransaction, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { usersRef, getUsersByCompanyId } from './read';
import { db } from '../../config';
import { auditAction, auditResourceType, userRole } from '../../../../constants/enum';
import { ValidationError, adminCountError } from '../../../../modules/error';
import { EditStaffType } from '../../../../components/uiParts/card/StaffEditCard';
import { deleteFile, uploadFile } from '../../storage';
import { handleUpdateUserResultData } from '../../function/handleUpdateUserResultData';
import { recordClientAuditEvent } from '../../function/security/clientAuditEventFunction';

/**
 * ユーザーの月間OCRページ上限を更新する（#2804 Phase4）
 * - limit が number（>0）: その値を上限として設定
 * - limit が null: 無制限（フィールドを削除）
 * 企業管理者のみが担当者編集画面から設定する想定
 */
export const updateUserMonthlyOcrLimit = async (
  uid: string,
  limit: number | null,
  updatedBy: string,
): Promise<void> => {
  const userDocRef = doc(usersRef, uid);
  await updateDoc(userDocRef, {
    usage_limit: limit !== null && limit > 0 ? limit : deleteField(),
    updated_at: serverTimestamp(),
    updated_by: updatedBy,
  });
};

/**
 * ユーザー情報を更新
 */
export const editStaffUser = async (
  uid: string,
  watch: <K extends keyof EditStaffType>(key: K) => EditStaffType[K],
  fileIcon: FileList | null,
): Promise<{ iconUrl: string | null }> => {
  const roleValue = watch('role');
  const role = typeof roleValue === 'boolean' ? roleValue : roleValue === 'true';
  const userDocRef = doc(usersRef, uid);

  // アイコンファイルがある場合は先にアップロード
  const fileIconName = fileIcon !== null ? fileIcon[0].name : '';
  const icon = fileIcon ? `users/${uid}/profile/${Date.now()}_${fileIconName}` : null;
  let uploadedIconData: { storagePath: string; iconUrl: string } | null = null;

  if (icon) {
    uploadedIconData = await uploadFile(fileIcon, icon);
  }

  // トランザクションでユーザー情報の読み取りと更新を原子的に実行
  const { oldFileIcon, oldIconUrl, roleChanged } = await runTransaction(db, async transaction => {
    const userDoc = await transaction.get(userDocRef);
    if (!userDoc.exists()) {
      throw new Error('ユーザーが見つかりません。');
    }

    const currentData = userDoc.data();
    const currentFileIcon = (currentData.icon as string) || '';
    const currentIconUrl = (currentData.icon_url as string) || '';
    const currentUserRole = currentData.role;

    // 管理者→メンバの場合に管理者数をチェック（会社単位）
    const companyId = currentData.company_id as string;
    if (currentUserRole === userRole.companyAdmin && role === false) {
      if (!companyId) {
        throw new Error('ユーザーの会社情報が取得できません。');
      }
      const companyUsers = await getUsersByCompanyId(companyId);
      const adminCount = companyUsers.filter(u => u.role === userRole.companyAdmin).length;

      if (adminCount <= 1) {
        throw new ValidationError(adminCountError);
      }
    }

    // 更新データの作成
    const baseUserData = {
      department: (watch('department') as string) || null,
      name: watch('name') as string,
      role: role ? userRole.companyAdmin : userRole.companyMember,
      updated_at: new Date(),
    };

    if (uploadedIconData) {
      transaction.update(userDocRef, {
        ...baseUserData,
        icon: uploadedIconData.storagePath,
        icon_url: uploadedIconData.iconUrl,
      });
    } else {
      transaction.update(userDocRef, baseUserData);
    }

    return {
      oldFileIcon: currentFileIcon,
      oldIconUrl: currentIconUrl,
      roleChanged: currentUserRole !== baseUserData.role,
    };
  });

  // トランザクション成功後に古いアイコンを削除
  if (uploadedIconData && oldFileIcon) {
    deleteFile(oldFileIcon);
  }

  // 監査ログ（#2985）: 権限変更（ロールが実際に変わった場合のみ）。fire-and-forget
  if (roleChanged) {
    void recordClientAuditEvent({
      action: auditAction.update,
      resource_type: auditResourceType.role,
      resource_id: uid,
    });
  }

  // resultのユーザー情報を更新（非同期で実行）
  const updatedUserName = watch('name') as string;
  const updatedIconUrl = uploadedIconData ? uploadedIconData.iconUrl : oldIconUrl || null;

  // バックグラウンドでresult更新を実行（エラーが発生しても担当者編集は成功とする）
  handleUpdateUserResultData(uid, updatedUserName, updatedIconUrl).catch(() => {
    // エラーが発生しても担当者編集は成功とする
  });

  return { iconUrl: updatedIconUrl as string | null };
};
